/** Window (ms) within which consecutive actions on the same layer are merged. */

import { defineStore } from 'pinia';
import type { EditorAction, LayerBatchAction } from '@/types/editor/action';
import { deepCloneRaw } from '@/helpers/clone';
import { createUid } from '@/utils/uid';
import { useElementEditStore } from '@/stores/elementEditStore';
import type { BatchHandle, BatchOperationEntry, EditorBatchHandle, EditorOperationEntry, HistoryActions, HistoryEntry, HistoryGetters, HistoryScope, HistorySnapshot, HistoryState, OperationEntry, ScopeConfig, ScopeState } from '@/types/stores/history';

const MERGE_WINDOW = 400;
/**
 * Structural actions replace object trees and carry ordering/selection effects.
 * Keep them as discrete history entries even when they happen close together.
 */
const NON_MERGE_ACTION_TYPES: ReadonlySet<EditorAction['type']> = new Set([
  'LAYER_GROUP',
  'LAYER_UNGROUP',
  'LAYER_REPARENT',
  'COMPONENT_DETACH'
]);

const SCOPE_CONFIGS: Record<HistoryScope, ScopeConfig> = {
  editor: { kind: 'action', maxSize: 200 },
  variant: { kind: 'action', maxSize: 20 },
  /** Element Edit session. Its own stack so the parked template's undo
   *  history survives the session untouched. */
  element: { kind: 'action', maxSize: 50 },
  /** AI Expand/Adapt modal. Discarded wholesale when the modal closes. */
  outpaint: { kind: 'snapshot', maxSize: 50 },
  /** Layerize studio. Collapses into editor history when the studio
   *  finishes; discarded on cancel. */
  studio: {
    kind: 'operation',
    maxSize: 25,
    captures: true,
    collapseInto: 'editor',
    collapseDescription: 'Studio operations'
  }
};

function createScopeState(): ScopeState {
  return {
    entries: [],
    pointer: -1,
    revision: 0,
    savedRevision: 0,
    lastRecordTime: 0,
    currentBatch: null
  };
}

/**
 * Scope a record lands in when the caller names none: an Element Edit session
 * owns every edit made while it runs, so those must not touch the parked
 * template's stack.
 */
function defaultScope(): HistoryScope {
  return useElementEditStore().active ? 'element' : 'editor';
}

/**
 * Scope a batch operation belongs to. The handle carries the scope its batch
 * was opened on, so a batch can never be closed against a different stack than
 * the one holding its buffer.
 */
function batchScope(handle: BatchHandle, scope?: HistoryScope): HistoryScope {
  return scope ?? handle.scope;
}

/**
 * Two actions can be merged if they share the same type, description,
 * target (layerId or adId), and the exact same set of edited keys. This
 * coalesces rapid repeated edits (e.g. arrow-key nudges) into a single
 * undo step.
 *
 * The keys have to match because a merge keeps the earlier action's inverse
 * and the later one's payload. Two edits that touch different properties
 * would leave an entry that redoes one and undoes the other — a description
 * is not enough to tell them apart, since several of them name only the layer
 * and the ads, never the property.
 *
 * For LAYER_BATCH, the two batches must target the exact same ordered
 * set of child actions so the preserved inverses line up correctly.
 */
function canMerge(a: EditorAction, b: EditorAction): boolean {
  if (a.type !== b.type) return false;
  if (a.description !== b.description) return false;
  if (NON_MERGE_ACTION_TYPES.has(a.type)) return false;

  if (a.type === 'LAYER_BATCH' && b.type === 'LAYER_BATCH') {
    const ac = a.payload.actions;
    const bc = b.payload.actions;
    if (ac.length !== bc.length) return false;
    return ac.every((child, i) => canMerge(child, bc[i]!));
  }

  const ap = a.payload as Record<string, unknown>;
  const bp = b.payload as Record<string, unknown>;
  if (!sameChangedKeys(ap.changes, bp.changes)) return false;
  if (ap.layerId && ap.layerId === bp.layerId) return true;
  if (ap.adId && ap.adId === bp.adId) return true;
  return false;
}

/** Both edit the same properties. Absent `changes` (a non-update action) passes. */
function sameChangedKeys(a: unknown, b: unknown): boolean {
  if (!isPlainRecord(a) || !isPlainRecord(b)) return a === undefined && b === undefined;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every(key => key in b);
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export const useHistoryStore = defineStore<
  'history',
  HistoryState,
  HistoryGetters,
  HistoryActions
>('history', {
  state: (): HistoryState => ({
    scopes: {
      editor: createScopeState(),
      variant: createScopeState(),
      element: createScopeState(),
      outpaint: createScopeState(),
      studio: createScopeState()
    },
    activeScope: 'editor',
    isBusy: false,
    interactionLocked: false,
    forcedDirty: false
  }),

  getters: {
    isDirty: state => {
      const editor = state.scopes.editor;
      const variant = state.scopes.variant;
      return (
        state.forcedDirty ||
        editor.revision !== editor.savedRevision ||
        variant.revision !== variant.savedRevision
      );
    },

    /** Unsaved changes in the running Element Edit session. */
    isElementDirty: state => {
      const s = state.scopes.element;
      return s.revision !== s.savedRevision;
    },

    /** Unsaved work ANYWHERE — the template, or a running Element session. */
    hasUnsavedWork: state => {
      const editor = state.scopes.editor;
      const variant = state.scopes.variant;
      const element = state.scopes.element;
      return (
        state.forcedDirty ||
        editor.revision !== editor.savedRevision ||
        variant.revision !== variant.savedRevision ||
        element.revision !== element.savedRevision
      );
    }
  },

  actions: {
    record(action: EditorAction, scope: HistoryScope = defaultScope()) {
      if (this.isBusy) return;
      assertKind(scope, 'action', 'record');

      /**
       * A capturing scope (the studio) swallows editor-action records made
       * while it is active — they become part of its transaction and reach
       * the editor stack only via collapseScope.
       */
      const active = this.activeScope;
      if (SCOPE_CONFIGS[active].captures && active !== scope) {
        // pushOperation clones the entry; passing the raw action here keeps
        // it a single structured clone per captured interaction.
        this.pushOperation({ kind: 'editor', action }, active);
        return;
      }

      const s = this.scopes[scope];

      if (s.currentBatch) {
        s.currentBatch.buffer.push(action);
        return;
      }

      const now = Date.now();
      const prev =
        s.pointer >= 0 ? (s.entries[s.pointer] as EditorAction) : undefined;

      /**
       * Merge into previous entry when the same type + layer within the window.
       * Keeps the original inverse (old values) and updates the payload (new values).
       */
      if (
        prev &&
        now - s.lastRecordTime < MERGE_WINDOW &&
        canMerge(prev, action)
      ) {
        const merged = deepCloneRaw(action);
        merged.inverse = prev.inverse;
        s.entries[s.pointer] = merged;
        s.lastRecordTime = now;
        s.revision++;
        return;
      }

      pushEntry(s, deepCloneRaw(action), SCOPE_CONFIGS[scope].maxSize);
      s.lastRecordTime = now;
      s.revision++;
    },

    /**
     * Record the current full state of a snapshot scope, dropping any redo
     * branch first. The stack is capped at the scope's maxSize.
     */
    pushSnapshot(snapshot: HistorySnapshot, scope: HistoryScope) {
      assertKind(scope, 'snapshot', 'pushSnapshot');
      const s = this.scopes[scope];
      pushEntry(s, deepCloneRaw(snapshot), SCOPE_CONFIGS[scope].maxSize);
    },

    /**
     * Record an operation entry. Only lands while the scope is active — an
     * operation scope is a live transaction, and pushes from stale handlers
     * after it deactivated must not resurrect it.
     */
    pushOperation(entry: OperationEntry, scope: HistoryScope) {
      assertKind(scope, 'operation', 'pushOperation');
      if (this.isBusy) return;
      if (this.activeScope !== scope) return;

      const s = this.scopes[scope];
      if (s.currentBatch) {
        s.currentBatch.buffer.push(deepCloneRaw(entry));
        return;
      }
      pushEntry(s, deepCloneRaw(entry), SCOPE_CONFIGS[scope].maxSize);
    },

    /**
     * Finish a transactional scope: fold every applied editor action it
     * captured into `collapseInto` as one LAYER_BATCH undo step, clear the
     * scope, and hand activation back. Cancel (discard) is `clear` +
     * `resetActiveScope` instead.
     */
    collapseScope(scope: HistoryScope) {
      const config = SCOPE_CONFIGS[scope];
      assertKind(scope, 'operation', 'collapseScope');
      if (!config.collapseInto) return;

      const s = this.scopes[scope];
      // Entries were cloned on the way in, and record() clones the compound
      // on the way out — no extra copy needed here.
      const applied = s.entries
        .slice(0, s.pointer + 1)
        .flatMap(entry => collectEditorActions(entry as OperationEntry));

      clearScope(s);
      if (this.activeScope === scope) this.activeScope = config.collapseInto;

      if (applied.length === 0) return;

      const compound: LayerBatchAction = {
        type: 'LAYER_BATCH',
        timestamp: Date.now(),
        description: config.collapseDescription ?? 'Batch',
        payload: { actions: applied },
        inverse: { actions: deepCloneRaw(applied).toReversed() }
      };
      this.record(compound, config.collapseInto);
    },

    /** Open a transactional/modal scope: fresh stack, activation taken. */
    activateScope(scope: HistoryScope) {
      clearScope(this.scopes[scope]);
      this.activeScope = scope;
    },

    /**
     * End a modal scope per its config: collapse into the parent when
     * `collapseInto` is declared, otherwise discard; either way hand
     * activation back.
     */
    deactivateScope(scope: HistoryScope) {
      if (SCOPE_CONFIGS[scope].collapseInto) {
        this.collapseScope(scope);
        return;
      }
      clearScope(this.scopes[scope]);
      if (this.activeScope === scope) this.resetActiveScope();
    },

    /**
     * For action/operation scopes, returns the entry to invert. For a
     * snapshot scope, returns the previous snapshot to re-apply (the entry at
     * pointer 0 is the scope's initial state, so undo stops above it).
     */
    undo(scope: HistoryScope = defaultScope()) {
      const s = this.scopes[scope];

      if (SCOPE_CONFIGS[scope].kind === 'snapshot') {
        if (s.pointer <= 0) return false;
        s.pointer--;
        return deepCloneRaw(s.entries[s.pointer]) ?? false;
      }

      if (s.pointer < 0) return false;
      const entry = deepCloneRaw(s.entries[s.pointer]);
      s.pointer--;
      s.revision++;
      return entry ?? false;
    },

    /** Returns the entry to re-apply (all kinds). */
    redo(scope: HistoryScope = defaultScope()) {
      const s = this.scopes[scope];
      if (s.pointer >= s.entries.length - 1) return false;

      s.pointer++;
      s.revision++;
      return deepCloneRaw(s.entries[s.pointer]) ?? false;
    },

    canUndo(scope: HistoryScope = defaultScope()) {
      const s = this.scopes[scope];
      return SCOPE_CONFIGS[scope].kind === 'snapshot'
        ? s.pointer > 0
        : s.pointer >= 0;
    },

    canRedo(scope: HistoryScope = defaultScope()) {
      const s = this.scopes[scope];
      return s.pointer < s.entries.length - 1;
    },

    scopeKind(scope: HistoryScope) {
      return SCOPE_CONFIGS[scope].kind;
    },

    setActiveScope(scope: HistoryScope) {
      this.activeScope = scope;
    },

    resetActiveScope() {
      this.activeScope = 'editor';
    },

    setInteractionLocked(value: boolean) {
      this.interactionLocked = value;
    },

    /**
     * Applies state changes without recording them: previews, restores, and
     * other transients that must never become undoable. Supports sync and
     * async `fn`; restores the previous busy flag even when `fn` throws.
     */
    runSilently<T>(fn: () => T): T {
      const wasBusy = this.isBusy;
      this.isBusy = true;

      let result: T;
      try {
        result = fn();
      } catch (error) {
        this.isBusy = wasBusy;
        throw error;
      }

      if (result instanceof Promise) {
        return result.finally(() => {
          this.isBusy = wasBusy;
        }) as T;
      }
      this.isBusy = wasBusy;
      return result;
    },

    clear(scope?: HistoryScope) {
      if (scope) {
        clearScope(this.scopes[scope]);
      } else {
        for (const key of Object.keys(this.scopes) as HistoryScope[]) {
          clearScope(this.scopes[key]);
        }
        this.activeScope = 'editor';
        this.interactionLocked = false;
        this.forcedDirty = false;
      }
    },

    /**
     * Pin the "saved" mark to the current revision. With a scope,
     * only that scope (an Element Edit save pins `element`). Without one, a
     * template save: every scope except `element`, which belongs to the
     * session and is saved separately. `forcedDirty` is an editor-scope
     * concept, so only a template/editor save clears it.
     */
    markSaved(scope?: HistoryScope) {
      const targets: HistoryScope[] = scope
        ? [scope]
        : (Object.keys(this.scopes) as HistoryScope[]).filter(
            key => key !== 'element'
          );
      for (const key of targets) {
        const s = this.scopes[key];
        s.savedRevision = s.revision;
      }
      if (!scope || scope === 'editor') this.forcedDirty = false;
    },

    markExternalChange() {
      const s = this.scopes.editor;
      s.entries = s.entries.slice(0, s.pointer + 1);
      s.lastRecordTime = 0;
      this.forcedDirty = true;
    },

    markUnsaved() {
      this.forcedDirty = true;
    },

    beginBatch(
      description: string,
      owner: string,
      scope: HistoryScope = defaultScope()
    ): BatchHandle | null {
      if (SCOPE_CONFIGS[scope].kind === 'snapshot') return null;
      // An operation scope only batches while it is the live transaction.
      if (
        SCOPE_CONFIGS[scope].kind === 'operation' &&
        this.activeScope !== scope
      ) {
        return null;
      }
      const s = this.scopes[scope];

      if (s.currentBatch) {
        return s.currentBatch.owner === owner
          ? {
              id: s.currentBatch.id,
              owner: s.currentBatch.owner,
              scope: s.currentBatch.scope
            }
          : null;
      }

      const handle: EditorBatchHandle = {
        id: createUid(),
        owner,
        scope
      };

      s.currentBatch = {
        ...handle,
        description,
        buffer: []
      };

      return handle;
    },

    endBatch(handle: BatchHandle, scope?: HistoryScope) {
      const target = batchScope(handle, scope);
      const s = this.scopes[target];
      if (!s.currentBatch || s.currentBatch.id !== handle.id) return;

      const { buffer: buffered, description } = s.currentBatch;
      s.currentBatch = null;

      if (buffered.length === 0) return;

      if (SCOPE_CONFIGS[target].kind === 'operation') {
        if (this.isBusy || this.activeScope !== target) return;
        // Buffer entries were cloned on insert — append without recloning.
        const entries = buffered as OperationEntry[];
        pushEntry(
          s,
          entries.length === 1
            ? entries[0]!
            : { kind: 'batch', description, entries },
          SCOPE_CONFIGS[target].maxSize
        );
        return;
      }

      const actions = buffered as EditorAction[];
      if (actions.length === 1) {
        this.record(actions[0]!, target);
        return;
      }

      const compound: LayerBatchAction = {
        type: 'LAYER_BATCH',
        timestamp: Date.now(),
        description,
        payload: { actions },
        inverse: { actions: actions.toReversed() }
      };
      this.record(compound, target);
    },

    cancelBatch(handle: BatchHandle, scope?: HistoryScope) {
      const s = this.scopes[batchScope(handle, scope)];
      if (!s.currentBatch || s.currentBatch.id !== handle.id) return;
      s.currentBatch = null;
    },

    /**
     * Close the batch and hand its buffer BACK to the caller instead of
     * recording it. Nothing enters the undo stack — the caller is expected to
     * wrap the actions into a composite entry of its own.
     */
    takeBatch(handle: BatchHandle, scope?: HistoryScope) {
      const s = this.scopes[batchScope(handle, scope)];
      if (!s.currentBatch || s.currentBatch.id !== handle.id) return [];

      const { buffer } = s.currentBatch;
      s.currentBatch = null;
      return buffer;
    },

    cancelCurrentBatch(scope: HistoryScope = defaultScope()) {
      this.scopes[scope].currentBatch = null;
    },

    isBatchOwnedBy(owner: string, scope: HistoryScope = defaultScope()): boolean {
      return this.scopes[scope].currentBatch?.owner === owner;
    },

    batchOpen(scope: HistoryScope = defaultScope()): boolean {
      return this.scopes[scope].currentBatch !== null;
    }
  }
});

/**
 * Private helpers
 */

function assertKind(
  scope: HistoryScope,
  kind: ScopeConfig['kind'],
  operation: string
): void {
  if (SCOPE_CONFIGS[scope].kind !== kind) {
    throw new Error(`${operation} requires a ${kind} scope, got '${scope}'`);
  }
}

/** Drop the redo branch, append, trim to `maxSize` keeping the pointer aligned. */
function pushEntry(s: ScopeState, entry: HistoryEntry, maxSize: number): void {
  s.entries = s.entries.slice(0, s.pointer + 1);
  s.entries.push(entry);
  s.pointer++;

  if (s.entries.length > maxSize) {
    s.entries.shift();
    s.pointer--;
  }
}

/** Editor actions applied by an operation scope, in recorded order. */
function collectEditorActions(entry: OperationEntry): EditorAction[] {
  if (entry.kind === 'editor') {
    return [(entry as EditorOperationEntry).action];
  }
  if (entry.kind === 'batch') {
    return (entry as BatchOperationEntry).entries.flatMap(collectEditorActions);
  }
  return [];
}

function clearScope(s: ScopeState): void {
  s.entries = [];
  s.pointer = -1;
  s.savedRevision = s.revision;
  s.currentBatch = null;
}
